import { useRouter } from 'next/router';
import React, { createContext, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { getResourceList } from '@/request/resources';
import { GetResourceListRequest, GetResourceListResponse } from '@/request/interface/resources';
import { capitalizeFirstLetter } from '@/utils/utils';
import useMount from '@/hooks/useMount';
import useIsMaxScreen from '@/hooks/useIsMaxScreen';
import useIsMediumScreen from '@/hooks/useIsMediumScreen';
import useResourcesStore from '@/store/useResourcesStore';
import { RootMenu, SubMenu } from './types';
import { findMenusByString } from './utils';
import ToastLoading from '../ToastLoading';

export interface Tag extends SubMenu {}

export interface Query {
  order?: 'publish' | 'view';
  page?: string;
}

interface ResourceContextProps {
  filterTags: Tag[];
  pushFilterTagAndUpdatePath: (tag: Tag) => void;
  setFilterTagsAndUpdatePath: (tags: Tag[]) => void;
  deleteFilterTagAndUpdatePath: (tag: Tag) => void;
  query: Query;
  setQueryAndUpdatePath: (query: Query) => void;
  setQuery: (query: Query) => void;
  resourceList: GetResourceListResponse | undefined;
}

interface ResourceProviderProps {
  children: React.ReactElement;
  menus: RootMenu[];
}

interface QueryParams {
  order?: 'publish' | 'view';
  page?: string;
}

const ResourceContext = createContext<ResourceContextProps>({
  filterTags: [],
  pushFilterTagAndUpdatePath: () => {},
  setFilterTagsAndUpdatePath: () => {},
  deleteFilterTagAndUpdatePath: () => {},
  query: {},
  setQueryAndUpdatePath: () => {},
  setQuery: () => {},
  resourceList: undefined,
});

export const ResourceProvider = ({ children, menus }: ResourceProviderProps) => {
  const router = useRouter();
  const { subject, tags, order, page } = router.query;
  const currentMenus = findMenusByString(tags as string, menus);
  const { setFilterTags, filterTags } = useResourcesStore((state) => {
    return { setFilterTags: state.setFilterTags, filterTags: state.filterTags };
  });
  const [query, setQuery] = useState<QueryParams>({
    ...(order ? { order: order as 'publish' | 'view' } : {}),
    ...(page ? { page: page as string } : {}),
  });
  const [resourceList, setResourceList] = useState<GetResourceListResponse>();
  const isMaxScreen = useIsMaxScreen();
  const isMediumScreen = useIsMediumScreen();

  useMount(() => {
    if (!currentMenus?.length && tags) {
      router.push(`/resources/${subject}/${window.location.search}`);
    }
    setFilterTags(currentMenus as Tag[]);
  });

  const getPageSize = () => {
    if (isMaxScreen) {
      return 16;
    }
    if (isMediumScreen) {
      return 12;
    }
    return 5;
  };

  const getResources = async (queryParams: QueryParams, tagList: Tag[]) => {
    const orderMap = {
      publish: 'LATEST_RELEASES',
      view: 'MOST_VIEWED',
    };
    const params = {
      sortingRule: orderMap[queryParams.order as 'publish' | 'view'],
      tagIdList: tagList?.filter((item) => item.menuType === 'Tag').map(({ id }) => id),
      themeId: tagList?.find((item) => item.menuType === 'Theme')?.id,
      topicId: tagList?.find((item) => item.menuType === 'Topic')?.id,
      pageNum: Number(queryParams.page || 1),
      pageSize: getPageSize(),
      subject: capitalizeFirstLetter(subject as string),
    };
    getResourceList(params as GetResourceListRequest)
      .then((res) => {
        setResourceList(res);
      })
      .finally(() => {
        ToastLoading.hide();
      });
  };

  const updateUrl = (
    currentTags: Tag[],
    queryParam: {
      order?: 'publish' | 'view' | undefined;
      page?: string;
    },
  ) => {
    setFilterTags(currentTags);
    setQuery(queryParam);
    getResources(queryParam, currentTags);

    const tagValues = currentTags?.map((item) => item.value);
    const tagPath = tagValues?.join('_');
    if (queryParam?.page === '1') {
      // eslint-disable-next-line no-param-reassign
      delete queryParam?.page;
    }
    if (queryParam?.order === undefined) {
      // eslint-disable-next-line no-param-reassign
      delete queryParam?.order;
    }
    let params = '';
    if (queryParam && Object.keys(queryParam)?.length) {
      params = new URLSearchParams(queryParam).toString();
    }
    router.push(`/resources/${subject}${tagPath ? `/${tagPath}` : ''}/${params ? `?${params}` : ''}`, undefined, {
      scroll: false,
      shallow: true,
    });
  };

  useMount(() => {
    getResources(query, filterTags);
  });

  useUpdateEffect(() => {
    getResources(query, filterTags);
  }, [subject]);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const providerValue = {
    filterTags,
    pushFilterTagAndUpdatePath: (tag: Tag) => {
      const tagList = filterTags.filter((item) => item.parentValue !== tag.parentValue);
      const currentTags = [...tagList, tag];
      const newQuery = { ...query, page: '1' };
      updateUrl(currentTags, newQuery);
    },
    setFilterTagsAndUpdatePath: (currentTags: Tag[]) => {
      const newQuery = { ...query, page: '1' };
      updateUrl(currentTags, newQuery);
    },
    deleteFilterTagAndUpdatePath: (tag: Tag) => {
      const currentTags = filterTags.filter((item) => item.value !== tag.value);
      const newQuery = { ...query, page: '1' };
      updateUrl(currentTags, newQuery);
    },
    query,
    setQueryAndUpdatePath: (value: Query) => {
      const newQuery = { ...query, ...value };
      updateUrl(filterTags, newQuery);
    },
    setQuery: (value: Query) => {
      const newQuery = { ...query, ...value };
      setQuery(newQuery);
    },
    resourceList: resourceList || undefined,
  };

  return <ResourceContext.Provider value={providerValue}>{children}</ResourceContext.Provider>;
};

export default ResourceContext;
